// booking/state/booking.context.tsx
import React, { useMemo, useReducer, useEffect, useCallback } from 'react'
import { useParams, useRouter, usePathname, useSearchParams } from 'next/navigation'
//import { useTranslations } from 'next-intl'

import {
  bookingReducer,
  initialBookingState,
  type BookingState,
  type BookingAction,
} from './booking.reducer'
import type { Cart, Stage } from '../types/booking'
import type { BookingConfig, BookingContextKey, StageId } from '../types/bookingConfig'
import { stageIdOf, stageNumberOf } from '../lib/bookingConfig'

//import { getTickets } from '../tickets'
//import { useBookingPersistence } from '../hooks/useBookingPersistence'
import { useBookingCartWatcher } from '../hooks/useBookingCartWatcher'
//import { useBookingRebuild } from '../hooks/useBookingRebuild'
//import { useBookingRouter } from '../hooks/useBookingRouter'
import { useBookingActions } from '../hooks/useBookingActions'
import { trackMoveStage } from '../services/bookingTracking'
import {
  breadcrumb,
  getOrCreateBookingAttemptId,
  setBookingContext,
  setBookingTags,
} from '../services/sentryBooking'
import { CustomerFormFields } from '../stages/CustomerDetails/types'

export interface BookingProviderProps {
  bookingConfig: BookingConfig
  children: React.ReactNode
}

export type BookingActionsShape = {
  dispatch: (action: BookingAction) => void
  trackHistory: (stage: Stage) => void
  onCompleteExtrasSelection: () => void
  onCompleteAddonsSelection: () => void
  onCompleteQuantitySelection: () => void
  onCompleteDateTime: () => void
  onCompleteCustomerData: (data: CustomerFormFields) => void
  rebuildCart: (cart?: Cart) => Promise<Cart | null | undefined>
}

const BookingStateContext = React.createContext<BookingState | null>(null)
const BookingActionsContext = React.createContext<BookingActionsShape | null>(null)

export function useBookingState() {
  const ctx = React.useContext(BookingStateContext)
  if (!ctx) throw new Error('useBookingState must be used inside BookingProvider')
  return ctx
}

export function useBookingActionsCtx() {
  const ctx = React.useContext(BookingActionsContext)
  if (!ctx) throw new Error('useBookingActionsCtx must be used inside BookingProvider')
  return ctx
}

export function BookingProvider(props: BookingProviderProps) {
  const params = useParams()
  const router = useRouter()
  const locale = (params?.locale as string | undefined) ?? 'en-GB'
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const { bookingConfig } = props
  /*console.log('params')
  console.log(params)*/

  const [state, dispatch] = useReducer(bookingReducer, initialBookingState)

  const trackHistory = useCallback(
    (stage: number) => {
      const stageId = stageIdOf(bookingConfig, stage) ?? stage.toString()
      const params = new URLSearchParams(searchParams.toString())
      params.set('stage', stageId)
      router.push(`?${params.toString()}`, {
        scroll: true,
      })
    },
    [router, searchParams, bookingConfig],
  )

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [state.stage])

  // Read ?flow= on mount and switch config if present
  useEffect(() => {
    dispatch({ type: 'SET_CONTEXT_KEY', contextKey: bookingConfig.context as BookingContextKey })
  }, [bookingConfig.context])

  // Sentry init on first render (client only behaviour is inside helper)
  useEffect(() => {
    const attemptId = getOrCreateBookingAttemptId()
    setBookingTags({ attemptId, locale: locale, route: pathname })
    setBookingContext({ started_at: new Date().toISOString() })
    breadcrumb('provider.init', { locale: locale, route: pathname }, 'booking.boot')
  }, [locale, pathname])

  useEffect(() => {
    const stageParam = searchParams.get('stage')
    if (!stageParam) return

    let current: Stage | null = null

    if (bookingConfig.stages.includes(stageParam as StageId)) {
      // New descriptive format: e.g. "quantity", "datetime"
      current = stageNumberOf(bookingConfig, stageParam as StageId) as Stage
    } else {
      // Fallback: legacy numeric format
      const asNum = Number(stageParam)
      if (!isNaN(asNum) && asNum > 0) current = asNum as Stage
    }

    if (current) dispatch({ type: 'SET_STAGE', stage: current })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  // stage tracking (GA) + Sentry tags/breadcrumbs
  useEffect(() => {
    if (state.stage === null) return
    setBookingTags({ stage: state.stage })
    breadcrumb('stage.changed', { stage: state.stage }, 'booking.flow')
    trackMoveStage(state.stage)
  }, [state.stage])

  // cart tagging (whenever it changes)
  useEffect(() => {
    if (!state.cart) return
    setBookingTags({ cartId: state.cart.id })
    setBookingContext({ cart_status: state.cart.status, cart_total: state.cart.total })
  }, [state.cart])

  // reset errors on stage change
  useEffect(() => {
    if (state.stage === null) return
    dispatch({ type: 'SET_ERROR', error: null })
  }, [state.stage])

  const actions = useBookingActions({ state, dispatch, trackHistory })
  const actionsValue: BookingActionsShape = useMemo(
    () => ({
      dispatch,
      trackHistory,
      ...actions,
    }),
    [dispatch, trackHistory, actions],
  )

  useBookingCartWatcher({
    state,
    actions: actionsValue,
    dispatch,
    trackHistory,
    locale,
    bookingConfig,
  })

  return (
    <BookingStateContext.Provider value={state}>
      <BookingActionsContext.Provider value={actionsValue}>
        {props.children}
      </BookingActionsContext.Provider>
    </BookingStateContext.Provider>
  )
}
